/**
 * Utility functions for Contentful
 *
 * Copyright (c) 2020 Coursera
 * Author: Kelvin Nguyen
 */
import React from 'react'
import isArray from 'lodash/isArray'
import { v4 as uuidv4 } from 'uuid'
import { BLOCKS } from '@contentful/rich-text-types';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import Image from 'components/Image'

const richTextOptions = {
  // Convert new lines into line breaks
  renderText: (text) => text.split('\n').flatMap((currText, i) => (
    [i > 0 && <br key={uuidv4()} />, currText]
  )),
  renderNode: {
    [BLOCKS.EMBEDDED_ASSET]: (node) => {
      const { file } = node.data.target.fields
      const imageFile = file?.['en-US'] || {}
      const contentType = imageFile.contentType || ''
      const fileName = imageFile.fileName || ''

      return (contentType.includes('image')) ? (
        <Image
          alt={fileName.split('.')[0]}
          src={imageFile.url}
          align="center" />
      ) : null
    },
  },
}

/**
 * Uses contentful's rich-text-react-renderer to automatically interpret
 *  the RichText into a react component. Because it also creates a top level component.
 * We want to strip this and let our CTAHeader handle this.
 * @param {Object} obj The current rich text object to parse
 * @param {boolean} shouldLeaveRoot The boolean that indicates whether to strip the root component
 */
export const convertRichTextToComponent = (obj, shouldLeaveRoot = false) => {
  if (!obj?.json) return null

  const id = obj.id || uuidv4()
  const reactEl = documentToReactComponents(obj.json, richTextOptions)

  if (shouldLeaveRoot) {
    return reactEl
  }

  if (isArray(reactEl) && reactEl.length > 0) {
    if (reactEl.length === 1) {
      return reactEl[0]?.props?.children
    }

    const allTextChildren = reactEl.map((el, index) => (
      // Only convert elements that are 'p'
      (el.type === 'p') ? (
        <React.Fragment key={`${id}-${index}`}>
          <span className="d-block">
            {el?.props?.children}
          </span>
        </React.Fragment>
      ) : el
    ))

    return allTextChildren
  }

  return reactEl?.props?.children
}
